import { backendApi } from "@/services/backend/api";
import {
  AddCertificateToOfferRequest,
  AddCompanyReferencesToOfferRequest,
  AddCompanyReferenceToOfferRequest,
  ApplyCalculationDataRequest,
  ChangeOfferDocumentProfileRequest,
  ClearSignatureRequest,
  CreateAndApplyCalculationDataRequest,
  CreateFromOfferRequest,
  CreateOfferConfigTextBlockSnapshotRequest,
  CreateRequest,
  DeleteOfferCompanyReferenceRequest,
  DeleteOfferConfigTextBlockSnapshotRequest,
  DeleteRequest,
  ListRequest,
  Offer,
  OfferSignature,
  OfferCoverLetterTextBlocks,
  OfferGeneralTextBlocksRes,
  OfferIsReadyForCustomerRequest,
  OfferList,
  PatchOfferConfigTextBlockSnapshotAlwaysDisplayRequest,
  PatchOfferConfigTextBlockSnapshotBodyRequest,
  PatchOfferConfigTextBlockSnapshotTitleRequest,
  PDFRequest,
  PreviewPDFRequest,
  PutCoverLetterTextBlocksRequest,
  PutGeneralTextBlocksRequest,
  PutOfferConfigSettingsSnapshotRequest,
  PutRequest,
  ReloadOfferConfigSnapshotRequest,
  RemoveCertificateFromOfferRequest,
  ReorderOfferConfigTextBlocksSnapshotRequest,
  SetOfferDateRequest,
  SetOfferContractTermsRequest,
  SetOfferCertifiedTrainingCoursePriceRequest,
  SetOfferValidUntilDateRequest,
  SetOfferImmutableRequest,
  ShowOfferRequest,
  SignRequest,
  UpdateFlyExterminatorMaintenanceDiscountRequest,
  UpdateHirePurchaseRequest,
  UpdateOfferCertificateRequest,
  UpdateOfferCompanyReferenceRequest,
  UpdatePostalAddressRequest,
  UpdateRepresentativeRequest,
  UpdateServiceAddressRequest,
} from "@/services/backend/qpm/offers/types/offer";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";

const qpmOfferApi = backendApi
  .enhanceEndpoints({
    addTagTypes: [
      "QpmOffer",
      "QpmOfferCoverLetterTextBlocks",
      "QpmOfferGeneralTextBlocks",
      "QpmOfferSignature",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      qpmOfferCreate: build.mutation<Offer, CreateRequest>({
        query: (request) => ({
          url: `/qpm/offer`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmOffer"],
      }),
      qpmOfferPut: build.mutation<Offer, PutRequest>({
        query: (request) => ({
          url: `/qpm/offer`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.id },
          "QpmOffer",
        ],
      }),
      qpmOfferShow: build.query<Offer, ShowOfferRequest>({
        query: (request) => ({
          url: `/qpm/offer`,
          method: "GET",
          params: request,
        }),
        providesTags: (result) =>
          result ? [{ type: "QpmOffer", id: result.id }] : [],
      }),
      qpmOfferList: build.query<OfferList, Partial<ListRequest>>({
        query: (request) => ({
          url: `/qpm/offer/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        transformResponse: (response: {
          offers?: Offer[];
          items?: Offer[];
          count?: number;
        }): OfferList => {
          const offers = response.items ?? response.offers ?? [];

          return {
            items: offers,
            count: response.count ?? offers.length,
          };
        },
        providesTags: (result) =>
          result ? [{ type: "QpmOffer", id: "LIST" }] : [],
      }),
      qpmOfferApplyCalculation: build.mutation<
        Offer,
        ApplyCalculationDataRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/apply-calculation`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmOffer"],
      }),
      qpmOfferSetDate: build.mutation<Offer, SetOfferDateRequest>({
        query: (request) => ({
          url: `/qpm/offer/set-date`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmOffer"],
      }),
      qpmOfferSetImmutable: build.mutation<Offer, SetOfferImmutableRequest>({
        query: (request) => ({
          url: `/qpm/offer/set-immutable`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.id },
          "QpmOffer",
        ],
      }),
      qpmOfferSetValidUntilDate: build.mutation<
        Offer,
        SetOfferValidUntilDateRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/set-valid-until-date`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmOffer"],
      }),
      qpmOfferSetCertifiedTrainingCoursePrice: build.mutation<
        Offer,
        SetOfferCertifiedTrainingCoursePriceRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/set-certified-training-course-price`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmOffer"],
      }),
      qpmOfferSetContractTerms: build.mutation<
        Offer,
        SetOfferContractTermsRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/set-contract-terms`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmOffer"],
      }),
      qpmOfferUpdatePostalAddress: build.mutation<
        Offer,
        UpdatePostalAddressRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/postal-address`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.id },
          "QpmOffer",
        ],
      }),
      qpmOfferUpdateServiceAddress: build.mutation<
        Offer,
        UpdateServiceAddressRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/service-address`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.id },
          "QpmOffer",
        ],
      }),
      qpmOfferUpdateRepresentative: build.mutation<
        Offer,
        UpdateRepresentativeRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/representative`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.id },
          "QpmOffer",
        ],
      }),
      qpmOfferUpdateHirePurchase: build.mutation<
        Offer,
        UpdateHirePurchaseRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/update-hire-purchase`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmOffer"],
      }),
      qpmOfferPutCoverLetterTextBlocks: build.mutation<
        OfferCoverLetterTextBlocks,
        PutCoverLetterTextBlocksRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/cover-letter-text-blocks`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: ["QpmOffer", "QpmOfferCoverLetterTextBlocks"],
      }),
      qpmOfferPutGeneralTextBlocks: build.mutation<
        OfferGeneralTextBlocksRes,
        PutGeneralTextBlocksRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/general-text-blocks`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: ["QpmOffer", "QpmOfferGeneralTextBlocks"],
      }),
      qpmOfferDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: `/qpm/offer`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: [{ type: "QpmOffer", id: "LIST" }],
      }),
      qpmOfferPdfPreview: build.query<{ objectURL: string }, PreviewPDFRequest>(
        {
          query: (request) => ({
            url: `/qpm/offer/pdf`,
            method: "PATCH",
            body: request,
            headers: { Accept: "application/pdf" },
            responseHandler: previewResponseHandler,
            // IMPORTANT. Since this query will download the PDF under no circumstances
            // should the results be cached. This could end up using the users memory.
            cache: "no-cache",
          }),
          providesTags: (_r, _e, request) => [
            { type: "QpmOffer", id: request.offerId },
          ],
        },
      ),
      qpmOfferPdfDownload: build.mutation<
        { filename: string; objectURL: string },
        PDFRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/pdf`,
          method: "PATCH",
          body: request,
          headers: { Accept: "application/pdf" },
          responseHandler: downloadResponseHandler,
          // IMPORTANT. Since this query will download the PDF under no circumstances
          // should the results be cached. This could end up using the users memory.
          cache: "no-cache",
        }),
        invalidatesTags: (_r, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
        ],
      }),
      qpmOfferSign: build.mutation<Offer, SignRequest>({
        query: (request) => {
          const body = new FormData();
          body.append("file", request.signature);
          return {
            url: `/qpm/offer/sign`,
            method: "PATCH",
            headers: {
              //  this is needed, otherwise a 415 error is returned
              Accept: "application/json",
            },
            params: {
              id: request.id,
              dataFormat: request.dataFormat,
              nameOfSigner: request.nameOfSigner,
              signatureLocation: request.signatureLocation,
              signatureDate: request.signatureDate,
            },
            body,
            formData: true,
          };
        },
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.id },
          "QpmOffer",
          { type: "QpmOfferSignature", id: request.id },
        ],
      }),
      qpmOfferClearSignature: build.mutation<Offer, ClearSignatureRequest>({
        query: (body) => ({
          url: "/qpm/offer/signature",
          method: "DELETE",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
          { type: "QpmOfferSignature", id: request.offerId },
        ],
      }),
      qpmOfferSignature: build.query<OfferSignature, ShowOfferRequest>({
        query: (request) => ({
          url: "/qpm/offer/signature",
          method: "GET",
          params: request,
        }),
        providesTags: (_result, _error, request) => [
          { type: "QpmOfferSignature", id: request.id },
        ],
      }),
      qpmOfferPutOfferConfigSettingsSnapshot: build.mutation<
        Offer,
        PutOfferConfigSettingsSnapshotRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/offer-config/settings`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferPatchOfferConfigTextBlockSnapshotBody: build.mutation<
        Offer,
        PatchOfferConfigTextBlockSnapshotBodyRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/offer-config/text-blocks/body`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferPatchOfferConfigTextBlockSnapshotTitle: build.mutation<
        Offer,
        PatchOfferConfigTextBlockSnapshotTitleRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/offer-config/text-blocks/title`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferPatchOfferConfigTextBlockSnapshotAlwaysDisplay: build.mutation<
        Offer,
        PatchOfferConfigTextBlockSnapshotAlwaysDisplayRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/offer-config/text-blocks/always-display`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferReorderOfferConfigTextBlocksSnapshot: build.mutation<
        Offer,
        ReorderOfferConfigTextBlocksSnapshotRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/offer-config/text-blocks/reorder`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferCreateOfferConfigTextBlockSnapshot: build.mutation<
        Offer,
        CreateOfferConfigTextBlockSnapshotRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/offer-config/text-blocks`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferDeleteOfferConfigTextBlockSnapshot: build.mutation<
        Offer,
        DeleteOfferConfigTextBlockSnapshotRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/offer-config/text-blocks`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferReloadOfferConfigSnapshot: build.mutation<
        Offer,
        ReloadOfferConfigSnapshotRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/offer-config/reload`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferAddCertificateToOffer: build.mutation<
        Offer,
        AddCertificateToOfferRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/certificates/add`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferRemoveCertificateFromOffer: build.mutation<
        Offer,
        RemoveCertificateFromOfferRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/certificates/remove`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferUpdateOfferCertificate: build.mutation<
        Offer,
        UpdateOfferCertificateRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/certificates`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferAddCompanyReferenceToOffer: build.mutation<
        Offer,
        AddCompanyReferenceToOfferRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/company-references/add`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferAddCompanyReferencesToOffer: build.mutation<
        Offer,
        AddCompanyReferencesToOfferRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/company-references`,
          method: "PUT",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferUpdateOfferCompanyReference: build.mutation<
        Offer,
        UpdateOfferCompanyReferenceRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/company-references`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferDeleteOfferCompanyReference: build.mutation<
        Offer,
        DeleteOfferCompanyReferenceRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/company-references`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferIsReadyForCustomer: build.mutation<
        Offer,
        OfferIsReadyForCustomerRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/is-ready-for-customer`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferChangeDocumentProfile: build.mutation<
        Offer,
        ChangeOfferDocumentProfileRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/document-profile`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
      qpmOfferCreateFromOffer: build.mutation<Offer, CreateFromOfferRequest>({
        query: (request) => ({
          url: `/qpm/offer/create-from-offer`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmOffer"],
      }),
      qpmOfferCreateAndApplyCalculation: build.mutation<
        Offer,
        CreateAndApplyCalculationDataRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/create-and-apply-calculation`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: ["QpmOffer"],
      }),
      qpmOfferUpdateFlyExterminatorMaintenanceDiscount: build.mutation<
        Offer,
        UpdateFlyExterminatorMaintenanceDiscountRequest
      >({
        query: (request) => ({
          url: `/qpm/offer/fly-exterminator-maintenance/discount`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "QpmOffer", id: request.offerId },
          "QpmOffer",
        ],
      }),
    }),
  });

export const {
  useQpmOfferCreateMutation,
  useQpmOfferShowQuery,
  useQpmOfferSignatureQuery,
  useQpmOfferPutMutation,
  useQpmOfferListQuery,
  useQpmOfferApplyCalculationMutation,
  useQpmOfferSetDateMutation,
  useQpmOfferSetImmutableMutation,
  useQpmOfferSetValidUntilDateMutation,
  useQpmOfferSetCertifiedTrainingCoursePriceMutation,
  useQpmOfferSetContractTermsMutation,
  useQpmOfferUpdatePostalAddressMutation,
  useQpmOfferUpdateServiceAddressMutation,
  useQpmOfferUpdateRepresentativeMutation,
  useQpmOfferUpdateHirePurchaseMutation,
  useQpmOfferDeleteMutation,
  useQpmOfferPutCoverLetterTextBlocksMutation,
  useQpmOfferPutGeneralTextBlocksMutation,
  useQpmOfferPdfPreviewQuery,
  useQpmOfferPdfDownloadMutation,
  useQpmOfferSignMutation,
  useQpmOfferClearSignatureMutation,
  useQpmOfferPutOfferConfigSettingsSnapshotMutation,
  useQpmOfferPatchOfferConfigTextBlockSnapshotBodyMutation,
  useQpmOfferPatchOfferConfigTextBlockSnapshotTitleMutation,
  useQpmOfferPatchOfferConfigTextBlockSnapshotAlwaysDisplayMutation,
  useQpmOfferReorderOfferConfigTextBlocksSnapshotMutation,
  useQpmOfferCreateOfferConfigTextBlockSnapshotMutation,
  useQpmOfferDeleteOfferConfigTextBlockSnapshotMutation,
  useQpmOfferReloadOfferConfigSnapshotMutation,
  useQpmOfferAddCertificateToOfferMutation,
  useQpmOfferRemoveCertificateFromOfferMutation,
  useQpmOfferUpdateOfferCertificateMutation,
  useQpmOfferAddCompanyReferenceToOfferMutation,
  useQpmOfferAddCompanyReferencesToOfferMutation,
  useQpmOfferUpdateOfferCompanyReferenceMutation,
  useQpmOfferDeleteOfferCompanyReferenceMutation,
  useQpmOfferIsReadyForCustomerMutation,
  useQpmOfferChangeDocumentProfileMutation,
  useQpmOfferCreateFromOfferMutation,
  useQpmOfferCreateAndApplyCalculationMutation,
  useQpmOfferUpdateFlyExterminatorMaintenanceDiscountMutation,
} = qpmOfferApi;
